"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FEATURE_IMPLIES = void 0;
exports.expandFeatures = expandFeatures;
/**
 * Feature implication map — the source of truth for which features a base
 * feature transitively grants. Used by every backend resolver that builds
 * `tenant.allowedFeatures`, plus the platform-app's `hasFeature` hook as
 * defense-in-depth.
 *
 * RULE: keys and values are FEATURE strings (never package IDs). This is
 * intentional — packages are admin-defined and vary across environments,
 * but feature gates are code-defined and must stay consistent.
 *
 * RULE: only ever ADD — adding a new key/value here grants a feature to
 * tenants that already pay for the base feature, never revokes one.
 *
 * RULE: an implied feature must be a UI affordance for the base, not an
 * upgrade. e.g. "pos.button.deals" is a UI surface for "pos.promos.basic"
 * (which the tenant already paid for); we do NOT imply paid modules.
 */
exports.FEATURE_IMPLIES = {
    // Anyone with the basic terminal package gets the core terminal action buttons.
    'pos.terminal.basic': [
        'pos.button.void',
        'pos.button.refund',
        'pos.button.exchange',
        'pos.button.discount',
        'pos.button.hold',
        'pos.button.open-order',
        'pos.button.search',
        'pos.button.end-shift',
        'pos.button.open-drawer',
        'pos.button.safe-drop',
        'pos.button.calculator',
        'pos.button.clock',
        'pos.button.shift-info',
        'pos.button.cash-tip',
    ],
    // Customer-facing buttons.
    'pos.customers.basic': ['pos.button.customer'],
    'pos.customers.loyalty': ['pos.button.rewards', 'pos.button.coupon'],
    // Promo / marketing buttons.
    'pos.promos.basic': ['pos.button.deals'],
    // Inventory / damage button (basic stock already paid for).
    'pos.stock.basic': ['pos.button.damage'],
    // Reports + transactions buttons.
    'pos.reports.basic': ['pos.button.reports', 'pos.button.transactions'],
    // Store supply addon.
    'store-supply': ['pos.button.store-supply'],
    // Services module — item catalog usage implies the POS terminal button.
    'pos.services.items': ['pos.button.services'],
};
/**
 * Returns a flat, deduplicated list containing every input feature plus
 * every feature transitively implied by `FEATURE_IMPLIES`. Idempotent —
 * calling twice produces the same set as once.
 */
function expandFeatures(base) {
    const result = new Set(base);
    // Single pass is sufficient because FEATURE_IMPLIES values never reference
    // other base keys (validated by the test "FEATURE_IMPLIES has no self-references").
    // If we ever need multi-level chains, switch to a worklist loop.
    for (const feature of base) {
        const implied = exports.FEATURE_IMPLIES[feature];
        if (implied) {
            for (const f of implied)
                result.add(f);
        }
    }
    return [...result];
}
//# sourceMappingURL=featureImplies.js.map